import React from "react";
import moment from "moment";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsModal from "../clients/Tools/kit/ToolsModal";
import ToolsTabs from "../clients/Tools/kit/ToolsTabs";
import ToolsPageHeader from "../clients/Tools/kit/ToolsPageHeader";
import PlaybookLibraryService from "../../services/PlaybookLibraryService";
import SkillLibraryService from "../../services/SkillLibraryService";
import ToolLibraryService from "../../services/ToolLibraryService";
import UtilityService from "../../services/UtilityService";
import AgentSkillBody from "./AgentSkillBody";
import AgentSkillTools from "./AgentSkillTools";
import AgentSkillEditor from "./AgentSkillEditor";
import PlaybookCategoryIcon from "./PlaybookCategoryIcon";

import "./agent-skill-page.css";

const EMPTY_DRAFT = { categoryId: null, name: "", description: "", body: "", toolNames: [], reads: [], changeNote: "" };

const TABS = {
	procedure: "procedure",
	versions: "versions",
};

class AgentSkillPage extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			skill: null,
			catalogue: [],
			categories: [],
			isLoading: true,
			loadError: null,
			activeTab: TABS.procedure,
			isEditing: props.isNew,
			draft: { ...EMPTY_DRAFT, ...(props.isNew && props.initialDraft ? props.initialDraft : {}) },
			isSaving: false,
			saveError: null,
			pendingReview: null,
			authorizationReason: "",
			isArchiveOpen: false,
			isArchiving: false,
			archiveError: null,
		};
	}

	componentDidMount() {
		this.fetchSkill();
	}

	fetchSkill = async () => {
		const { skillId, isNew } = this.props;
		try {
			const skillRequest = isNew ? Promise.resolve(null) : SkillLibraryService.getSkill({ skillId });
			const [skill, catalogue, categories] = await Promise.all([skillRequest, ToolLibraryService.listToolCatalogue(), PlaybookLibraryService.listCategories()]);
			await this.update({ skill, catalogue, categories, isLoading: false, loadError: null });
		} catch (error) {
			await this.update({ isLoading: false, loadError: UtilityService.describeRequestError({ error, fallback: "This skill could not be loaded." }) });
		}
	};

	onStartEditing = () => {
		const { skill } = this.state;
		const draft = {
			categoryId: skill.category ? skill.category.id : null,
			name: skill.name,
			description: skill.description || "",
			body: skill.body || "",
			toolNames: [...(skill.toolNames || [])],
			reads: [...(skill.reads || [])],
			changeNote: "",
		};
		this.update({ isEditing: true, draft, saveError: null });
	};

	onDraftChanged = patch => {
		this.update({ draft: { ...this.state.draft, ...patch }, pendingReview: null });
	};

	onCancelEditing = () => {
		const { isNew, onBack } = this.props;
		if (isNew) {
			onBack();
			return;
		}
		this.update({ isEditing: false, saveError: null, pendingReview: null });
	};

	onSave = async ({ authorization } = {}) => {
		const { isNew, onSaved } = this.props;
		const { skill, draft } = this.state;
		await this.update({ isSaving: true, saveError: null });
		try {
			const saved = await SkillLibraryService.saveSkill({
				skillId: isNew ? undefined : skill.id,
				categoryId: draft.categoryId,
				name: draft.name.trim(),
				description: draft.description.trim(),
				body: draft.body,
				toolNames: draft.toolNames,
				reads: draft.reads,
				changeNote: draft.changeNote.trim(),
				authorization,
			});
			if (saved.needsAuthorization) {
				await this.update({ isSaving: false, pendingReview: saved, authorizationReason: "" });
				return;
			}
			await this.update({ skill: saved, isSaving: false, isEditing: false, pendingReview: null });
			if (isNew) onSaved({ skillId: saved.id });
		} catch (error) {
			await this.update({ isSaving: false, saveError: UtilityService.describeRequestError({ error, fallback: "The skill was not saved. Try again." }) });
		}
	};

	onAuthorizeSave = () => {
		const { authorizationReason } = this.state;
		this.onSave({ authorization: { reason: authorizationReason.trim() } });
	};

	onDismissReview = () => {
		this.update({ pendingReview: null, authorizationReason: "" });
	};

	onOpenArchive = () => {
		this.update({ isArchiveOpen: true, archiveError: null });
	};

	onCloseArchive = () => {
		this.update({ isArchiveOpen: false });
	};

	onArchive = async () => {
		const { skill } = this.state;
		await this.update({ isArchiving: true, archiveError: null });
		try {
			await SkillLibraryService.archiveSkill({ skillId: skill.id });
			await this.update({ isArchiving: false, isArchiveOpen: false });
			this.props.onBack();
		} catch (error) {
			await this.update({ isArchiving: false, archiveError: UtilityService.describeRequestError({ error, fallback: "The skill was not archived." }) });
		}
	};

	renderFacts() {
		const { onOpenPlaybook } = this.props;
		const { skill, catalogue } = this.state;
		const latestVersion = (skill.versions || [])[0];
		const savedByText = latestVersion && latestVersion.savedBy ? ` by ${latestVersion.savedBy}` : "";
		const credits = [
			skill.category ? skill.category.name : null,
			skill.isBuiltin ? "Maintained by SRJ" : `Written by ${skill.createdBy}`,
			latestVersion ? `v${skill.versionNumber} saved${savedByText} ${moment(latestVersion.savedAt).fromNow()}` : null,
		];
		const readFirstNames = (skill.reads || []).map(read => read.tool);
		const usedBy = skill.usedBy || [];

		return (
			<React.Fragment>
				<div className="tools-page-header__credits">{credits.filter(Boolean).join(" · ")}</div>
				<AgentSkillTools
					label="Brings"
					toolNames={skill.toolNames}
					catalogue={catalogue}
					readFirstNames={readFirstNames}
					emptyText="Brings no tools; it guides how an agent works."
				/>
				<div className="tk-chips">
					<span className="tk-chips__label">Used by</span>
					{usedBy.length === 0 && <span className="tk-muted">no playbook yet</span>}
					{usedBy.map(use => (
						<button
							key={use.playbookId}
							type="button"
							className="tk-chip tk-chip--muted tk-chip--link"
							onClick={() => onOpenPlaybook({ playbookId: use.playbookId })}
							title="Open the playbook"
						>
							<Icon.Cpu size={12} />
							{use.name}
						</button>
					))}
				</div>
			</React.Fragment>
		);
	}

	renderHeader() {
		const { onBack, isNew } = this.props;
		const { skill, isEditing } = this.state;
		const canEdit = !!skill && skill.mayEdit && !skill.isBuiltin;
		const title = isNew ? "New skill" : skill.name;
		const subtitle = isNew ? "Write the know-how once; the guide offers it to playbooks that need it." : skill && !isEditing ? skill.description : null;

		return (
			<ToolsPageHeader
				onBack={onBack}
				backLabel="Back to skills"
				lead={
					skill && skill.category ? (
						<PlaybookCategoryIcon icon={skill.category.icon} size="large" />
					) : (
						<span className="tk-icon-tile tk-icon-tile--lg">
							<Icon.BookOpen />
						</span>
					)
				}
				title={title}
				badges={
					skill ? (
						<React.Fragment>
							<span className={skill.isBuiltin ? "tk-badge tk-badge--accent" : "tk-badge tk-badge--neutral"}>
								{skill.isBuiltin ? "Built-in" : "Custom"}
							</span>
							<span className="tk-badge tk-badge--neutral">v{skill.versionNumber}</span>
						</React.Fragment>
					) : null
				}
				subtitle={subtitle}
				facts={skill && !isEditing ? this.renderFacts() : null}
				actions={
					canEdit && !isEditing ? (
						<React.Fragment>
							<button type="button" className="tk-text-action tk-text-action--muted" onClick={this.onOpenArchive}>
								Archive
							</button>
							<button type="button" className="tk-button tk-button--primary" onClick={this.onStartEditing}>
								<Icon.Edit2 size={14} />
								Edit
							</button>
						</React.Fragment>
					) : null
				}
			/>
		);
	}

	renderVersion = version => {
		const { skill } = this.state;
		const isCurrent = version.versionNumber === skill.versionNumber;

		return (
			<div key={version.versionNumber} className="tk-table__row tk-table__row--body agent-skill-page__version-row">
				<span className="agent-skill-page__version-number">
					<span className="tk-table__primary">v{version.versionNumber}</span>
					{isCurrent && <span className="tk-badge tk-badge--accent">Current</span>}
				</span>
				<span className="tk-table__stack">
					<span>{version.changeNote}</span>
					{version.review && (
						<span className="agent-skill-page__version-check">
							<span
								className={`tk-badge tk-badge--${SkillLibraryService.describeSkillReview({ review: version.review }).tone}`}
								title={version.review.reason}
							>
								{version.authorizedBy ? "Authorized change" : SkillLibraryService.describeSkillReview({ review: version.review }).label}
							</span>
							{version.authorizedBy && (
								<span className="tk-table__meta">
									{version.authorizedBy}: {version.authorizationReason}
								</span>
							)}
						</span>
					)}
				</span>
				<span className="tk-table__meta">{version.savedBy}</span>
				<span className="tk-table__meta tk-table__cell--end">{moment(version.savedAt).format("MMM D, YYYY h:mm a")}</span>
			</div>
		);
	};

	renderVersions() {
		const { skill } = this.state;

		return (
			<div className="tk-table tk-card">
				<div className="tk-table__row tk-table__row--head agent-skill-page__version-row">
					<span className="tk-table__head-cell">Version</span>
					<span className="tk-table__head-cell">What changed and why</span>
					<span className="tk-table__head-cell">Saved by</span>
					<span className="tk-table__head-cell tk-table__cell--end">When</span>
				</div>
				{(skill.versions || []).map(this.renderVersion)}
			</div>
		);
	}

	renderArchiveModal() {
		const { skill, isArchiveOpen, isArchiving, archiveError } = this.state;
		if (!skill) return null;
		const footer = (
			<div className="tk-modal-actions">
				<button type="button" className="tk-button tk-button--secondary" onClick={this.onCloseArchive}>
					Cancel
				</button>
				<button type="button" className="tk-button tk-button--danger" onClick={this.onArchive} disabled={isArchiving}>
					{isArchiving ? "Archiving…" : "Archive skill"}
				</button>
			</div>
		);
		return (
			<ToolsModal open={isArchiveOpen} onClose={this.onCloseArchive} title={`Archive ${skill.name}?`} width={520} footer={footer}>
				<p className="agent-skill-page__archive-text">It leaves the firm's Skills list and can no longer be added to a playbook.</p>
				{archiveError && (
					<div className="tk-notice tk-notice--danger">
						<div className="tk-notice__title">
							<Icon.AlertTriangle size={14} />
							{archiveError}
						</div>
					</div>
				)}
			</ToolsModal>
		);
	}

	render() {
		const { isNew } = this.props;
		const {
			skill,
			catalogue,
			categories,
			isLoading,
			loadError,
			activeTab,
			isEditing,
			draft,
			isSaving,
			saveError,
			pendingReview,
			authorizationReason,
		} = this.state;
		if (isLoading) return <div className="tk-empty">Loading skill…</div>;
		if (loadError) return <div className="tk-notice tk-notice--danger">{loadError}</div>;
		const tabs = [
			{ key: TABS.procedure, label: "Procedure" },
			{ key: TABS.versions, label: "Versions", count: skill ? (skill.versions || []).length : undefined },
		];

		return (
			<div className="agent-skill-page">
				{this.renderHeader()}
				{isEditing && (
					<AgentSkillEditor
						draft={draft}
						original={skill}
						catalogue={catalogue}
						categories={categories}
						isNew={isNew}
						isSaving={isSaving}
						saveError={saveError}
						pendingReview={pendingReview}
						authorizationReason={authorizationReason}
						onChange={this.onDraftChanged}
						onAuthorizationReasonChange={({ reason }) => this.update({ authorizationReason: reason })}
						onAuthorize={this.onAuthorizeSave}
						onDismissReview={this.onDismissReview}
						onSave={() => this.onSave()}
						onCancel={this.onCancelEditing}
					/>
				)}
				{!isEditing && skill && (
					<React.Fragment>
						<ToolsTabs tabs={tabs} activeKey={activeTab} onSelect={tab => this.update({ activeTab: tab })} />
						{activeTab === TABS.procedure && skill.maySeeProcedure && (
							<section className="tk-card agent-skill-page__body">
								<AgentSkillBody body={skill.body} pageTitle={skill.name} />
							</section>
						)}
						{activeTab === TABS.procedure && !skill.maySeeProcedure && (
							<section className="tk-card agent-skill-page__private">
								<span className="tk-icon-tile tk-icon-tile--neutral">
									<Icon.Lock />
								</span>
								<span className="agent-skill-page__private-text">
									<span className="tk-text-strong">{skill.isBuiltin ? "SRJ's procedure is proprietary." : "This procedure is private."}</span>
									{skill.isBuiltin
										? "Playbooks follow it as written; the text itself is not shown to the firm."
										: "Only the person who wrote it and administrators can read or change it. Playbooks follow it as written."}
								</span>
							</section>
						)}
						{activeTab === TABS.versions && this.renderVersions()}
					</React.Fragment>
				)}
				{this.renderArchiveModal()}
			</div>
		);
	}
}

export default AgentSkillPage;
